import { type EmbedTargetType } from './embed-target'
import { JwPlayerMediaControls } from '../media/JwPlayerMediaControls'
import { type MediaControls } from '../media'
import { type DetectEmbedTargetsOptions, type EmbedTargetFactory } from './embed-target-factory'
import { BaseEmbedTarget } from './base'
import { waitUntil } from '../../utils/wait-until'
import { type EmbedBeforeCreateElementContext, type EmbedElementContext } from '../element-embedder'
import { type IFrameEmbedHandler } from '../embed-strategies/iframe/IFrameEmbedStrategy'
import { DefaultEmbedStyleControl } from '../iframe/default-embed-style-control'
import { throttle } from 'lodash-es'
import { FloatStrategyHandler } from '../embed-strategies/iframe/FloatStrategyHandler'
import { logger } from '../../utils/logger'
import { type EmbedIFrameComponent } from '../embed-component'

export class JwPlayerTargetFactory implements EmbedTargetFactory<JwPlayerEmbedTarget> {
  name: EmbedTargetType = 'jwplayer'
  supports (ctx: EmbedBeforeCreateElementContext): boolean {
    return window.jwplayer != null && ctx.targetEl.classList.contains('jwplayer')
  }

  async ready () {
    await waitUntil(() => (window.jwplayer != null) && (document.querySelector('.jwplayer') != null) && window.jwplayer().getContainer())
  }

  create (ctx: EmbedElementContext): JwPlayerEmbedTarget {
    return new JwPlayerEmbedTarget(ctx)
  }

  detectFromDom (_options: DetectEmbedTargetsOptions): HTMLElement[] {
    if (window.jwplayer != null) {
      return Array.from(document.querySelectorAll('.jwplayer')) // TODO test multiple instances
    } else return []
  }
}

/**
 * Attempts to inject the iframe into the jwplayer container.
 * Warning:
 * - `overflow: visible` must be set when `teleport` is enabled to allow the iframe to be visible. This might cause issues with the jwplayer.
 */
class JwPlayerIFrameEmbedHandler implements IFrameEmbedHandler {
  #controlBarElObserver: ResizeObserver | undefined
  iframeControl: DefaultEmbedStyleControl
  iFrameComponent: EmbedIFrameComponent

  constructor (public embedTarget: JwPlayerEmbedTarget) {}

  async initIFrame (iFrameComponent: EmbedIFrameComponent) {
    if (window.jwplayer == null) {
      logger.error('jwplayer is not loaded.')
      return
    }

    this.iframeControl = new DefaultEmbedStyleControl(this.embedTarget, this.iFrameComponent.rootEl)
    this.iFrameComponent = iFrameComponent

    let jwMediaEl: Element
    try {
      jwMediaEl = await waitUntil<Element>(() => {
        return this.embedTarget.ctx.targetEl.querySelector('.jw-media')
      })
    } catch (err) {
      logger.error('Could not detect jwplayer instantiation.', err)
      return
    }
    // This is required to show ee during teleport mode
    if (this.embedTarget.ctx.settings?.teleport?.disabled === true) {
      this.embedTarget.ctx.targetEl.style.setProperty('overflow', 'visible')
    }

    const controlBarEl = this.embedTarget.ctx.targetEl.querySelector('.jw-controlbar')
    if (controlBarEl != null) {
      const observer = new ResizeObserver(throttle(() => { this.onTeleportUpdate().catch(logger.error) }, 100))
      observer.observe(controlBarEl)
      this.#controlBarElObserver = observer
    }
    this.onTeleportUpdate().catch(logger.error)
    jwMediaEl.after(this.iFrameComponent.rootEl)
  }

  async onTeleportUpdate () {
    this.iframeControl.updateStyle(
      this.embedTarget.ctx.teleportActive === true ? 'teleport' : 'overlay'
    )
  }

  destroy (): void {
    this.#controlBarElObserver?.disconnect?.()
  }

  show (): void {
    this.iFrameComponent.rootEl.style.setProperty('display', '')
  }

  hide (): void {
    this.iFrameComponent.rootEl.style.setProperty('display', 'none')
  }
}

export class JwPlayerEmbedTarget extends BaseEmbedTarget {
  mediaControl?: MediaControls
  constructor (ctx: EmbedElementContext) {
    super('jwplayer', ctx)
    this.mediaControl = new JwPlayerMediaControls(this.ctx.targetEl)
  }

  protected getIFrameEmbedHandler (): IFrameEmbedHandler {
    const strategy = this.ctx.settings.strategy
    if (strategy == null || strategy === 'parent') return new JwPlayerIFrameEmbedHandler(this)
    else if (strategy === 'float-absolute' || strategy === 'float-fixed') return new FloatStrategyHandler(this)
    throw new Error('Invalid strategy `' + strategy + '` for HTMLMediaEmbedTarget')
  }

  get bottomSpace (): string {
    const controlBarEl = this.ctx.targetEl?.querySelector?.('.jw-controlbar')
    if ((controlBarEl != null) && controlBarEl instanceof HTMLElement && controlBarEl.offsetHeight != null) {
      return `${controlBarEl.offsetHeight}px`
    }
    return super.bottomSpace
  }

  setPlaysInline (_value: boolean): void { }
}
